import React, { useCallback, useState, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import AccountDisplay from '../../components/account-display/account-display'
import BeneficiaryDisplay from '../../components/beneficiary-display/beneficiary-display'
import NumberFormat from 'react-number-format'
import NeoButton from '../../components/NeoButton/NeoButton'
import { Formik, Form, Field } from 'formik'
import NeoInput from '../../components/NeoInput/NeoInput'
import InputError from '../../components/InputError/InputError'
import NeoSuccess from '../../components/FormComponents/NeoSuccess'
import NeoOTP from '../../components/FormComponents/NeoOTP'
import Loading from 'custom/assets/loaders/Loading'
import TermsAndCondition from '../../components/TermsAndConditions/TermsAndCondition'
import {
  getCurrentAccDetailsSelector,
  getRoleAccessKeySelector,
} from '../../store/selectors/app.selector'
import { setIsAcctRefresh } from '../../store/actions/app.actions'
import { OtherTransferSchema } from '../../schemas/transfers'
import LayoutWithAccountCard from '../Layout/layout-account-card'
import TransferService from '../../services/transfer.service'
import moment from 'moment'
import {
  BUTTON_KEYS,
  BENEFICIARY_TRANSFER_KEYS,
  VALIDATION_KEYS,
} from '../../utils/translation-constants'
import { useTranslation } from 'react-i18next'
import validationConfig from 'custom/config/validation.config.json'
const domesticTransferConfig = validationConfig['transfers']['domestic']
import config from 'custom/config/environment'
import QuickLinkService from '../../services/quick-link.service'
import AuthService from '../../services/auth.service'

export default function OtherAccountTransfer() {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [stage, setStage] = useState(1)
  const [otp, setOtp] = useState('')
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [beneDetails, setBeneDetails] = useState({})
  const [formValues, setFormValues] = useState({})
  const [verifyDetails, setVerifyDetails] = useState({})
  const [transferType, setTransferType] = useState({})
  const { t } = useTranslation()
  const [requestDetails, setRequestDetails] = useState({})
  const currentAccountDetail = useSelector(getCurrentAccDetailsSelector)
  const accessKey = useSelector(getRoleAccessKeySelector)
  const [successResponse, setSuccessResponse] = useState({})
  const [getUserDetails, setUserDetails] = useState({})

  useEffect(() => {
    if (location.state === undefined) {
      history.push('/transfers')
      return
    }
    setBeneDetails(location?.state?.beneDetails)
    setTransferType(location?.state?.transferType)
    getRights()
  }, [])

  const getRights = useCallback(async () => {
    try {
      setLoading(true)
      const response = await AuthService.getRights()
      if (response.status === 200) {
        setUserDetails(response?.data?.user)
      }
    } catch (err) {
      setLoading(false)
    }
  }, [])

  const onVerify = useCallback(
    async (values) => {
      try {
        setLoading(true)
        setFormValues(values)
        if (values.transferAmount === '0.00' || values.transferAmount === '.00') {
          return
        }
        const reqParams = {
          transferReq: {
            fin: {
              srcAccount: currentAccountDetail.accountNo,
              srcAccBranch: currentAccountDetail.brnCode,
              srcAccCcy: currentAccountDetail.currency,
              destAccount: beneDetails.beneAccNo,
              destAccBranch: beneDetails.beneBrn,
              destAccCcy: beneDetails.beneCurrency || '',
              destBankName: beneDetails.bankName,
              destBankCountry: beneDetails.bankCountry || '',
              destBankAddress: beneDetails.bankAddress,
              beneAddress: '',
              amount: values.transferAmount,
              txnCurrency: values.txnCurrency,
              bicCode: beneDetails?.bicCode || '',
              sortCode: beneDetails?.sortCode || '',
              remarks: values.remarks,
              chargesBorneBy: '',
              txnDate: '',
            },
            nfin: {
              transferType: values.transferType,
              beneId: beneDetails.reqId,
              scheduled: values.isScheduled === 'Yes' ? true : false,
              scheduledDate: values.scheduledDate || '',
              beneficiaryName: beneDetails.beneName,
              beneficiaryMail: beneDetails.beneEMail,
              beneficiaryMobile: beneDetails.beneMobileNo,
              addBeneficiary: false,
              bulkTransfer: false,
            },
            cdf: {
              FXREFNO: '',
              add1:getUserDetails?.homeAddress||"",
              mobileNo: getUserDetails?.mobileNo||"",
            },
          },
        }
        const response = await TransferService.externalVerifyFundTransfer(reqParams)
        if (response.status === 200) {
          setOtpEnabled(response.data['otpEnable'])
          setVerifyDetails(response.data)
          setRequestDetails(reqParams)
          setStage(2)
          setLoading(false)
        }
      } catch (err) {
        setLoading(false)
        setMessage(err?.data)
      }
    },
    [beneDetails, currentAccountDetail,getUserDetails],
  )

  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const reqParams = {
        ...requestDetails,
        otp: otp || '0000',
        randomKey: verifyDetails.randomKey,
      }
      const response = await TransferService.externalInitiateTransfer(reqParams)
      if (response.status === 200) {
        dispatch(setIsAcctRefresh(true))
        setSuccessResponse(response.data)
        setMessage(response.data['message'])
        setStage(4)
        setLoading(false)
      }
    } catch (err) {
      setMessage(err?.data)
      setOtp('')
      setLoading(false)
    }
  }, [otp, formValues, verifyDetails, requestDetails])

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirm()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return (
          <NeoSuccess
            onClick1={async () =>
              await QuickLinkService.pdfReceipt({
                transRefNum: successResponse.referenceNo,
                refId: '',
                module: 'FUNDTRANSFER',
              })
            }
            module='DOWNLOAD_RECEIPT'
            roleKey={accessKey}
            message={message}
            linkPath='/transfers'
          />
        )
      default:
        return renderForm()
    }
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function renderForm() {
    return (
      <>
        <AccountDisplay currentAccountDetail={currentAccountDetail} />
        <p style={{ fontSize: 10, fontWeight: 600, paddingLeft: 30 }}>To</p>
        <BeneficiaryDisplay beneDetails={beneDetails} />
        <Formik
          initialValues={{
            transferAmount: '',
            remarks: '',
            txnCurrency: currentAccountDetail?.currency,
            transferType: config.TRANSFER_TYPES.includes('DDACC') ? 'DDACC' : 'RTGS',
            // customRemarks: '',
            // beneAlert: 'All',
            isScheduled: 'No',
            scheduledDate: '',
          }}
          enableReinitialize
          validationSchema={OtherTransferSchema}
          onSubmit={onVerify}>
          {({ isSubmitting, errors, touched, setFieldValue, handleChange, values }) => (
            <Form>
              <div>
                <label style={{ display: 'block' }} className='control-label'>
                  {t(BENEFICIARY_TRANSFER_KEYS.TRANSFER_TYPE)}
                </label>
                {config.TRANSFER_TYPES.includes('DDACC') && (
                  <div className='custom-control custom-radio custom-control-inline pt-20'>
                    <Field
                      as='input'
                      type='radio'
                      id='transferTypeSelect1'
                      name='transferType'
                      value='DDACC'
                      className='custom-control-input'
                    />
                    <label className='custom-control-label' htmlFor='transferTypeSelect1'>
                      {t(BENEFICIARY_TRANSFER_KEYS.DDACC)}
                    </label>
                  </div>
                )}
                {config.TRANSFER_TYPES.includes('RTGS') && (
                  <div className='custom-control custom-radio custom-control-inline'>
                    <Field
                      as='input'
                      type='radio'
                      id='transferTypeSelect2'
                      name='transferType'
                      value='RTGS'
                      className='custom-control-input'
                    />
                    <label className='custom-control-label' htmlFor='transferTypeSelect2'>
                      {t(BENEFICIARY_TRANSFER_KEYS.RTGS)}
                    </label>
                  </div>
                )}
              </div>

              <div className='form-group'>
                <NeoInput
                  tabIndex={'1'}
                  showAsterisk={domesticTransferConfig['isAmountRequired']}
                  text={t(BENEFICIARY_TRANSFER_KEYS.TRANSFER_AMOUNT)}
                  name='amount'
                  onChange={(values) => {
                    setFieldValue('transferAmount', values.value)
                  }}
                  mode='amount'
                />
                <InputError text={errors.transferAmount} touched={touched.transferAmount} />
                <InputError
                  text={t(VALIDATION_KEYS.TRANSFER_AMOUNT_VALIDATION)}
                  touched={values.transferAmount === '0.00' || values.transferAmount === '.00'}
                />
              </div>
              {/* <div className='mt-3 form-group'>
                <NeoInput
                  showAsterisk={domesticTransferConfig['isBeneAlertRequired']}
                 text={t(BENEFICIARY_TRANSFER_KEYS.BANK_ALERT)}
                  mode='select'
                  name='beneAlert'
                  onChange={(e) => {
                    handleChange(e)
                  }}
                  list={smsAlertList}
                  keyValue={'label'}
                />
                <InputError text={errors.beneAlert} touched={touched.beneAlert} />
              </div> */}
              {config.SCHEDULED_TRANSFERS && (
                <>
                  <div className='mt-3 form-group'>
                    <NeoInput
                      tabIndex={'2'}
                      showAsterisk={domesticTransferConfig['isSchedulePaymentRequired']}
                      text={t(BENEFICIARY_TRANSFER_KEYS.SCHEDULED_PAYMENT)}
                      mode='select'
                      name='isScheduled'
                      onChange={(e) => {
                        handleChange(e)
                      }}
                      list={['Yes', 'No']}
                      keyValue={null}
                    />
                    <InputError text={errors.isScheduled} touched={touched.isScheduled} />
                  </div>

                  {values.isScheduled === 'Yes' && (
                    <div className='form-group mt-3'>
                      <NeoInput
                        tabIndex={'3'}
                        name='scheduledDate'
                        showAsterisk={domesticTransferConfig['isScheduleDateRequired']}
                        text={t(BENEFICIARY_TRANSFER_KEYS.SCHEDULED_DATE)}
                        icon='calendar_today'
                        mode='single'
                        type='date'
                        onChange={handleChange}
                        min={moment(new Date()).format('YYYY-MM-DD')}
                      />
                      <InputError text={errors.scheduledDate} touched={touched.scheduledDate} />
                    </div>
                  )}
                </>
              )}
              <div className='mt-3 form-group'>
                <NeoInput
                  tabIndex={'4'}
                  text={t(BENEFICIARY_TRANSFER_KEYS.REMARKS)}
                  showAsterisk={domesticTransferConfig['isRemarksRequired']}
                  onChange={handleChange}
                  name='remarks'
                />
                <InputError text={errors.remarks} touched={touched.remarks} />
              </div>

              <div className='mt-3'>
                {isSubmitting ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    tabIndex={'5'}
                    type='submit'
                    mode={'newDesign'}
                    disabled={isSubmitting}
                    text={t(BUTTON_KEYS.SUBMIT)}
                  />
                )}
              </div>
              <div className='back-cancel-btn-container'>
                <button
                  tabIndex={'6'}
                  className='back-cancel-btn btn btn-sm new-design-warning-text-color'
                  onClick={() => history.goBack()}>
                  {t(BUTTON_KEYS.CANCEL)}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </>
    )
  }

  function renderConfirm() {
    return (
      <>
        <AccountDisplay currentAccountDetail={currentAccountDetail} />
        <p style={{ fontSize: 10, fontWeight: 600, paddingLeft: 30 }}>To</p>
        <BeneficiaryDisplay beneDetails={beneDetails} />

        <div className='d-flex flex-column confirmation'>
          <div>
            <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.TRANSFER_TYPE)}</h6>
            <h6 className='value'>
              {formValues?.transferType === 'DDACC'
                ? t(BENEFICIARY_TRANSFER_KEYS.DDACC)
                : t(BENEFICIARY_TRANSFER_KEYS.RTGS)}
            </h6>
          </div>
          <div>
            <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.TRANSFER_AMOUNT)}</h6>
            <h6 className='value'>{formValues?.transferAmount}</h6>
          </div>
          <div>
            <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.CHARGES)}</h6>
            <h6 className='value'>
              <NumberFormat
                value={verifyDetails?.charges || 0}
                displayType={'text'}
                thousandSeparator={true}
                prefix={''}
                fixedDecimalScale={true}
                decimalScale={2}
                allowEmptyFormatting
              />
            </h6>
          </div>
          <div>
            <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.CURRENCY)}</h6>
            <h6 className='value'>{formValues.txnCurrency}</h6>
          </div>

          {/* <div>
            <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.BANK_ALERT)}</h6>
            <h6 className='value'>{formValues?.beneAlert}</h6>
          </div> */}
          {config.SCHEDULED_TRANSFERS && (
            <>
              <div>
                <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.SCHEDULED_PAYMENT)}</h6>
                <h6 className='value'>{formValues?.isScheduled}</h6>
              </div>
              {formValues.isScheduled === 'Yes' && (
                <div>
                  <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.SCHEDULED_DATE)}</h6>
                  <h6 className='value'>{moment(formValues.scheduledDate).format('DD-MM-YYYY')}</h6>
                </div>
              )}
            </>
          )}
          <div>
            <h6 className='label'>{t(BENEFICIARY_TRANSFER_KEYS.REMARKS)}</h6>
            <h6 className='value'>
              {formValues.remarks === 'Custom' ? formValues.customRemarks : formValues.remarks}
            </h6>
          </div>

          <div>
            <TermsAndCondition module='BENEFICIARYTRANSFER' />
          </div>

          <div className='mt-3'>
            {loading ? (
              renderLoading()
            ) : (
              <NeoButton
                mode='newDesign'
                type='button'
                className='btn btn-neo'
                onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
                text={t(BUTTON_KEYS.CONFIRM)}
              />
            )}
          </div>
          <div className='back-cancel-style' onClick={() => setStage(1)}>
            {t(BUTTON_KEYS.BACK)}
          </div>
        </div>
      </>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='TRANSFER_OTHER'>
      <div className='new-design-form-style'>
        <div className='heading-style'>{t(BENEFICIARY_TRANSFER_KEYS.TITLE_DOMESTIC)}</div>

        <div>{renderSwitch()}</div>
      </div>
    </LayoutWithAccountCard>
  )
}
